import * as React from 'react'
import { useHistory, useLocation } from 'react-router-dom'

function useQuery(): URLSearchParams {
  return new URLSearchParams(useLocation().search)
}

export type UpdateURLSearchParamsType =
  | [string, string | void]
  | [string, string]

export const useUpdateURLSearchParams = (): ((
  ...params: UpdateURLSearchParamsType[]
) => void) => {
  const location = useLocation()
  const history = useHistory()

  return (...params: UpdateURLSearchParamsType[]) => {
    const searchParams = new URLSearchParams(location.search)
    params.forEach(([key, value]) => {
      if (value) {
        const decodedValue = decodeURIComponent(value)
        searchParams.set(key, decodedValue)
      } else {
        searchParams.delete(key)
      }
    })

    const newSearch = decodeURIComponent(searchParams.toString())
    history.replace({
      hash: location.hash,
      search: newSearch,
    })
  }
}

export type ToQueryObjectProps<T = Record<string, any>> = {
  queryParams?: URLSearchParams
  excludeKeys?: string[]
  defaultValues?: Partial<T>
}

export const toQueryObject = <T = Record<string, any>>(
  params?: ToQueryObjectProps<T>
): Partial<T> => {
  const filters: Partial<T> = {}

  params?.queryParams?.forEach((value, key) => {
    if (params?.excludeKeys?.includes(key)) return
    if (!value) return

    if (['true', 'false'].includes(value)) {
      filters[key as keyof T] = (value === 'true') as any
    } else {
      filters[key as keyof T] = value as any
    }
  })

  return filters
}

export type UseFiltersResult<T> = {
  filters: Partial<T>
}

export const useFilters = <T = Record<string, any>>({
  defaultValues,
  ...props
}: ToQueryObjectProps<T> = {}): UseFiltersResult<T> => {
  const location = useLocation()
  const history = useHistory()
  const searchParams = location.search
  const params = React.useMemo(
    () => new URLSearchParams(searchParams),
    [searchParams]
  )

  const filters = toQueryObject<T>({
    queryParams: params,
    ...props,
  })

  React.useEffect(() => {
    if (params.toString() === '' && defaultValues) {
      const defaultParams = new URLSearchParams()
      Object.entries(defaultValues).forEach(([key, value]) => {
        if (value !== undefined) {
          defaultParams.set(key, String(value))
        }
      })
      history.replace({
        search: defaultParams.toString(),
      })
    }
  }, [history, params, defaultValues])

  return { filters }
}

export default useQuery
